import React, { useState } from "react";
import { Mutation, MutationFn } from "react-apollo";
import gql from "graphql-tag";
import { StarIcon, CopyIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Center,
  Heading,
  Image,
  Alert,
  AlertIcon,
  Container,
} from "@chakra-ui/react";
import SalesAdminFolderSelect from "./SalesAdminFolderSelect";
import UserSelect from "./UserSelect";

const CopyFavoritesFolderMutation = gql`
  mutation CopyFavoritesFolder($userId: String!, $folderId: String!) {
    copyFavoritesFolder(userId: $userId, folderId: $folderId) {
      id
      response {
        payload
        message
      }
    }
  }
`;

interface FormMessage {
  payload: string;
  message: string;
}

interface CopyMutationResult {
  copyFavoritesFolder: {
    id: string;
    response: FormMessage;
  };
}

interface CopyMutationVariables {
  userId: string;
  folderId: string;
}

type FormResponse = FormMessage | null;

export interface SelectOption {
  label: string;
  value: string;
}

export type SelectedOption = SelectOption | null;

const AssignFavorites = (): JSX.Element => {
  const [selectedUser, setSelectedUser] = useState<SelectedOption>(null);
  const [selectedFolder, setSelectedFolder] = useState<SelectedOption>(null);
  const [onCopyMessage, setOnCopyMessage] = useState<FormResponse>(null);

  return (
    <Container maxW={"1250px"}>
      <Heading size={"lg"}>
        <StarIcon w={6} h={6} paddingBottom={1} />
        Assign Favorites
      </Heading>
      <Box
        bg="#F7FAFC"
        border="1px"
        borderColor="#A0AEC0"
        borderRadius="5px"
        p="20px"
      >
        {onCopyMessage?.payload && (
          <Alert
            data-cy="copy-favorites-message"
            mb={5}
            color={onCopyMessage.payload === "copySuccess" ? "green" : "red"}
            status={
              onCopyMessage.payload === "copySuccess" ? "success" : "error"
            }
          >
            <AlertIcon />
            {onCopyMessage.message}
          </Alert>
        )}
        <Box mb={8} display={[null, null, "flex"]}>
          <SalesAdminFolderSelect
            onFolderChange={setSelectedFolder}
            selectedFolder={selectedFolder}
          />
          <Center>
            <Image
              display={["none", "none", "block"]}
              src={`${process.env.S3BUCKET}/images/copy_icon.png`}
            />
          </Center>
          <UserSelect
            onUserChange={setSelectedUser}
            selectedUser={selectedUser}
          />
        </Box>
        <Mutation mutation={CopyFavoritesFolderMutation}>
          {(
            copyFavoritesFolder: MutationFn<
              CopyMutationResult,
              CopyMutationVariables
            >
          ) => {
            return (
              <Button
                cursor="pointer"
                data-cy="copy-favorites-button"
                _hover={{ bg: "#004C6E" }}
                rightIcon={<CopyIcon />}
                color="white"
                border={"none"}
                bg="#5B646D"
                onClick={async (e) => {
                  e.preventDefault();

                  if (!selectedUser?.value || !selectedFolder?.value) {
                    setOnCopyMessage({
                      payload: "formError",
                      message: "Must select a user and a folder to copy.",
                    });
                    return;
                  }

                  const { data } = await copyFavoritesFolder({
                    variables: {
                      userId: selectedUser.value,
                      folderId: selectedFolder.value,
                    },
                  });

                  if (data?.copyFavoritesFolder) {
                    setOnCopyMessage(data.copyFavoritesFolder.response);
                  }

                  if (
                    data?.copyFavoritesFolder.response.payload === "copySuccess"
                  ) {
                    setSelectedFolder(null);
                    setSelectedUser(null);
                  }
                }}
              >
                Copy Favorites Folder
              </Button>
            );
          }}
        </Mutation>
      </Box>
    </Container>
  );
};

export default AssignFavorites;
